// ./src/components/print/cardGeometry.ts
//
// WHERE A CARD LANDS ON ITS STOCK. Pure arithmetic, and deliberately the only thing in this file.
//
// ⚠ IT HAS NO IMPORTS AT ALL, ON PURPOSE. It started life inside `PrintCardPage.tsx`, where it could
// only ever be checked by a regex reading the source — and a regex can prove a formula is PRESENT, never
// that it is RIGHT. Pulling it out so a guard could execute it was not enough on its own: importing it
// from the component dragged in the whole React tree and a CSS module, which a plain tsx script cannot
// load. So the input is described STRUCTURALLY here rather than imported from the API types. `PrintMedia`
// satisfies it by shape, TypeScript checks that at every call site, and `npm run check-print-sheets` can
// run the real function against real stock.
//
// ⚠ EVERYTHING IS MILLIMETRES. See the API's boundary/print.ts for why that is a correctness decision
// and not a style one: a cell 0.4mm out prints across a perforation.

/** The geometry a layout needs. `PrintMedia` matches this shape; nothing else is required. */
export interface CardMediaGeometry {
  columnCount?: number | null;
  rowCount?: number | null;
  cellWidthMm?: number | null;
  cellHeightMm?: number | null;
  gutterXMm?: number | null;
  gutterYMm?: number | null;
  marginLeftMm?: number | null;
  marginTopMm?: number | null;
  pageWidthMm?: number | null;
  pageHeightMm?: number | null;
  /** Per-stock extras. `stubWidthMm` is the only one read here — see `stubWidthMm()` below. */
  settings?: Record<string, any> | null;
}

/**
 * A per-SHEET correction, in millimetres, held in the browser rather than the database.
 *
 * `x`/`y` shift the whole grid — that is the printer's own unprintable margin, which differs per machine.
 *
 * ⚠ `gx`/`gy` ADJUST THE GUTTER, and they exist because a uniform shift cannot fix a wrong gap. If the
 * first card lands correctly and the last one does not, the error is compounding per column or row, and
 * only the gutter can absorb it. Avery publishes each card's SIZE and the count per sheet but not the
 * offsets between them, so any stock whose gutters were derived rather than measured needs this — and
 * the host, holding the actual sheet and a ruler, is the only one who can supply the number.
 */
export interface Nudge {
  x: number;
  y: number;
  /** Added to the stock's own horizontal gutter. */
  gx?: number;
  /** Added to the stock's own vertical gutter. */
  gy?: number;
}

/** Where one cell sits on its sheet, in millimetres. */
export interface CellBox {
  leftMm: number;
  topMm: number;
  widthMm: number;
  heightMm: number;
}

export const num = (value: unknown, fallback = 0): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

/** How many cards one page of this stock holds. 1 for a roll, where the label IS the page. */
export const cellsPerPage = (media?: CardMediaGeometry | null): number =>
  Math.max(1, num(media?.columnCount, 1) * num(media?.rowCount, 1));

/**
 * THE ARITHMETIC THE WHOLE FEATURE RESTS ON: where cell `index` of a page lands on this stock.
 *
 * `index` is the cell's position WITHIN its page (0 … cols×rows−1); the caller paginates. Order is
 * ROW-MAJOR — across the first row, then down — because that is the order a human pulls tickets off a
 * sheet, and transposing it would print every card in the wrong cell while looking entirely normal.
 *
 * The three ways to get this wrong, all invisible on screen and all ruinous on die-cut stock:
 *   - counting n gutters instead of (n − 1) walks each column right until the last prints off the page;
 *   - omitting the margin shifts the entire sheet, so a stock with an inset first cell tears wrong;
 *   - using the page size where the cell size belongs makes every card the size of the paper.
 * Each has a check in `npm run check-print-sheets` that RUNS this function and asserts the millimetres.
 */
export const cellBox = (
  media: CardMediaGeometry | null | undefined,
  index: number,
  nudge: Nudge = { x: 0, y: 0 },
): CellBox => {
  const cols = Math.max(1, num(media?.columnCount, 1));
  const cellWidth = num(media?.cellWidthMm, num(media?.pageWidthMm, 0));
  const cellHeight = num(media?.cellHeightMm, num(media?.pageHeightMm, 0));
  const col = index % cols;
  const row = Math.floor(index / cols);

  // ⚠ The gutter correction multiplies with the column/row index, which is exactly why it cannot be
  // expressed as a shift: the error it fixes grows across the sheet rather than sitting still.
  const gutterX = num(media?.gutterXMm) + num(nudge.gx);
  const gutterY = num(media?.gutterYMm) + num(nudge.gy);

  return {
    leftMm: num(media?.marginLeftMm) + col * (cellWidth + gutterX) + nudge.x,
    topMm: num(media?.marginTopMm) + row * (cellHeight + gutterY) + nudge.y,
    widthMm: cellWidth,
    heightMm: cellHeight,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// CARD CONTENT SIZING — how big the type and the QR are INSIDE one cell.
//
// ⚠ IT LIVES HERE, BESIDE THE CELL MATHS, FOR THE SAME REASON: it is arithmetic whose failure is
// invisible until something is rendered at a size nobody tried. The first version sized the QR from the
// cell's HEIGHT alone with no cap, which was fine on a 43mm Avery cell and produced a **198mm** QR on a
// 190mm-wide full-page ticket — overflowing the card and collapsing the text column beside it to nothing.
// ─────────────────────────────────────────────────────────────────────────────

/** The Avery 16154 cell these sizes were chosen against. Everything scales from it. */
export const REFERENCE_CELL_HEIGHT_MM = 43.18;

/**
 * ⚠ THE QR MAY NEVER TAKE MORE THAN THIS SHARE OF THE CARD'S WIDTH. The text beside it is in a
 * `flex: 1, minWidth: 0` column, which means it does not push back — it silently collapses to zero and
 * the card prints as a bare QR with no name, no type and no code. The share is the text's protection.
 */
export const QR_MAX_WIDTH_SHARE = 0.4;

/** Below this a QR of a 10-character code stops surviving a fold and a cheap scanner. */
export const QR_MIN_MM = 8;

/**
 * ⚠ AND AN UPPER BOUND, which is the part that is easy to leave out. Scannability depends on MODULE
 * size against reading distance, and past roughly this the QR stops getting easier to read and just
 * takes room from the ticket. A full-page ticket wants a big QR, not an enormous one.
 */
export const QR_MAX_MM = 45;

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

/** Type scale for a cell, relative to the Avery reference. Clamped — below ~0.55 nothing is readable. */
export const cardScale = (cellHeightMm: number): number =>
  clamp(num(cellHeightMm, REFERENCE_CELL_HEIGHT_MM) / REFERENCE_CELL_HEIGHT_MM, 0.55, 1.8);

/** The card's inner padding, in mm. */
export const cardPadMm = (cellHeightMm: number): number => Math.max(1.2, 2.5 * cardScale(cellHeightMm));

const QR_HEIGHT_SHARE: Record<string, number> = { sm: 0.45, md: 0.62, lg: 0.78 };

/**
 * ⚠ How much of the card's WIDTH the QR may take, by layout. Beside the text it must leave a usable
 * column (0.4); stacked BENEATH the text it is the full-width element and the constraint moves to
 * height instead, so it may be much wider.
 */
const QR_WIDTH_SHARE_BY_LAYOUT: Record<CardLayout, number> = { wide: QR_MAX_WIDTH_SHARE, square: 0.62, tall: 0.72 };

/** And how much of the HEIGHT, since a stacked QR shares the vertical run with the text above it. */
const QR_HEIGHT_FACTOR_BY_LAYOUT: Record<CardLayout, number> = { wide: 1, square: 0.62, tall: 0.5 };

/**
 * The side of the QR square, in millimetres, for one cell.
 *
 * ⚠ BOUNDED BY BOTH AXES, not just height — that omission is what produced a QR wider than its own card.
 * It takes the SMALLER of "a share of the content height" and "a share of the content width", then
 * clamps to a readable minimum and a useful maximum.
 */
export const qrSizeMm = (cellWidthMm: number, cellHeightMm: number, qrSizeKey = 'md', stacked = false): number => {
  const w = num(cellWidthMm);
  const h = num(cellHeightMm);
  /**
   * ⚠⚠ THE SHARES BELONG TO THE ARRANGEMENT, NOT TO THE SHAPE. A `wide` card's QR may take the full
   * content height because it sits BESIDE the text — but a wide card that STACKS (a folded tent card,
   * or a large ticket laid out as a column) has the QR sharing the vertical run with everything else,
   * and sizing it from the shape alone took 38mm of a 62mm card and collapsed every band above it to
   * nothing. `stacked` says which of the two is happening; the geometry cannot tell.
   */
  const layout = stacked && cardLayout(w, h) === 'wide' ? 'square' : cardLayout(w, h);
  const pad = cardPadMm(h);
  const contentHeight = Math.max(0, h - 2 * pad);
  const contentWidth = Math.max(0, w - 2 * pad);
  const byHeight =
    contentHeight * (QR_HEIGHT_SHARE[qrSizeKey] ?? QR_HEIGHT_SHARE.md) * QR_HEIGHT_FACTOR_BY_LAYOUT[layout];
  const byWidth = contentWidth * QR_WIDTH_SHARE_BY_LAYOUT[layout];

  const wanted = Math.min(byHeight, byWidth);

  // ⚠⚠ NO FLOOR THAT OVERRIDES THE SHARES. A cell too small to carry BOTH a scannable QR and its text
  // gets NO QR at all, and prints the code as text for the door to type.
  //
  // The first version raised anything under the minimum UP to it, which re-created the exact problem
  // the width share exists to stop: on a 20 × 20mm card an 8mm QR is 47% of the width, the text column
  // beside it (`flex: 1, minWidth: 0`) silently collapses, and the card prints as a bare QR with no
  // name, no type and no code. Caught by the property check in `check-print-sheets`, which sweeps cell
  // sizes nobody would think to try — the catalog's smallest stock is 54 × 25mm and passes comfortably.
  //
  // ⚠ Between "an unscannable QR", "a starved card" and "no QR", the last is the only one that still
  // produces a usable ticket.
  if (wanted < QR_MIN_MM) {
    return 0;
  }

  return Math.min(wanted, QR_MAX_MM);
};

// ─────────────────────────────────────────────────────────────────────────────
// CARD SHAPE — which internal layout a cell's PROPORTIONS call for.
//
// ⚠⚠ ONE LAYOUT CANNOT SERVE EVERY STOCK, and assuming it could is what produced an unreadable sheet.
// The original card put the text in a column on the left and the QR beside it on the right, which is
// right for an Avery 16154 ticket (139.7 × 43.18mm — three times wider than it is tall) and absurd
// everywhere else: on a 2.5" square card it leaves each text line about 25mm, so every line ellipsises
// to "Net…", and on a tall 16431 ticket (54 × 140mm) it does the same while wasting 100mm of height.
//
// A cell's PROPORTIONS decide where the QR goes. Beside the text when there is width to spare; beneath
// it when there is not.
// ─────────────────────────────────────────────────────────────────────────────

export type CardLayout = 'wide' | 'square' | 'tall';

/**
 * ⚠ The thresholds are about whether the TEXT still has a usable column once the QR takes its share,
 * not about aesthetics. Below roughly 2:1 the remaining width stops fitting a name and a ticket type on
 * one line, which is the point at which stacking beats sitting side by side.
 */
export const cardLayout = (cellWidthMm: number, cellHeightMm: number): CardLayout => {
  const ratio = num(cellWidthMm) / Math.max(0.001, num(cellHeightMm));

  if (ratio >= 2) {
    return 'wide';
  }

  return ratio <= 0.85 ? 'tall' : 'square';
};

/** The stock's tear-off width in mm, or 0 when this paper has no perforation. */
export const stubWidthMm = (media?: CardMediaGeometry | null): number => num(media?.settings?.stubWidthMm);

/**
 * ⚠⚠ A STUB IS A PROPERTY OF THE PAPER, NOT OF THE DESIGN. The tear-off exists because the sheet is
 * perforated at a fixed offset; drawing one on stock that has no perforation prints a dashed line and a
 * duplicated code across the middle of a solid card, which is exactly what "Ticket with stub" on Avery
 * 16431 and 35702 produced. Only 16154 declares a stub, so only 16154 gets one — the design ASKS for a
 * stub and the stock decides whether there is one to draw.
 */
export const drawsStub = (media: CardMediaGeometry | null | undefined, designWantsStub: boolean): boolean =>
  designWantsStub && stubWidthMm(media) > 0;

/**
 * HOW MUCH OF A CELL THE TEAR-OFF TAKES — a width on a wide card, a height on a stacked one, 0 on stock
 * with no perforation.
 *
 * ⚠⚠ IT IS A PROPERTY OF THE PAPER, SO IT APPLIES WHETHER OR NOT ANYTHING WANTS TO DRAW A STUB. That is
 * the correction: a NAME BADGE printed on Avery 16154 has no tear-off of its own, so it was laid out
 * across the whole cell — straight over a perforation the sheet is physically split along. The badge is
 * then in two pieces the moment anybody separates the tickets. A ticket DESIGN chooses whether to
 * decorate the stub (`drawsStub`); nothing gets to choose whether the paper is perforated.
 */
export const stubExtentMm = (media?: CardMediaGeometry | null): number => {
  const stub = stubWidthMm(media);

  if (stub <= 0) {
    return 0;
  }

  const w = num(media?.cellWidthMm);
  const h = num(media?.cellHeightMm);

  return Math.min(cardLayout(w, h) !== 'wide' ? h * 0.3 : w * 0.45, stub);
};

/**
 * THE BOX A CARD'S CONTENT MAY ACTUALLY USE — the cell, minus the fold and minus the perforation.
 *
 * ⚠ A folded card's face is HALF the cell (Avery 5011 scores across the middle), and planning a grid
 * against the whole cell runs the content through the score line.
 */
/**
 * The proportions of ONE PRINTED FACE, width ÷ height — what artwork is actually arranged against.
 *
 * ⚠⚠ A FOLDED CARD IS TWO CARDS, AND THE FACE IS HALF THE CELL (owner-reported, 2026-09-23). An Avery
 * 5011 place card scores across the middle and stands as a tent, so `BadgeCard` renders the face twice —
 * once upright, once rotated — each `foldMm` tall, with the artwork inside EACH one. An editor that
 * previewed the whole CELL was therefore showing a box twice as tall as the thing being printed, and a
 * host arranging artwork in it saw something different from what came out of the printer.
 *
 * ⚠ It is the FULL face, stub included, unlike `contentFaceMm`: the artwork is full-bleed and runs under
 * the tear-off strip, while the text slots are kept off it.
 */
export const cardFaceAspect = (media?: CardMediaGeometry | null): number => {
  const w = num(media?.cellWidthMm);
  const h = cardFolds(media) ? foldMm(media) : num(media?.cellHeightMm);

  return w > 0 && h > 0 ? w / h : 1;
};

export const contentFaceMm = (media?: CardMediaGeometry | null): { widthMm: number; heightMm: number } => {
  const w = num(media?.cellWidthMm);
  const cellH = num(media?.cellHeightMm);
  const h = cardFolds(media) ? foldMm(media) : cellH;
  const stub = stubExtentMm(media);
  const stacked = cardLayout(w, cellH) !== 'wide';

  return stacked
    ? { widthMm: w, heightMm: Math.max(0, h - stub) }
    : { widthMm: Math.max(0, w - stub), heightMm: h };
};

/**
 * How much width the TEXT actually gets, in mm — which is the number the layout change was about.
 *
 * ⚠ THE QR'S SIZE WAS NEVER THE POINT. Stacking the QR beneath the details barely changes how big the
 * QR is; what it changes is that the text stops sharing the width with it. On a 2.5" square card the
 * side-by-side layout left the text ~32mm — which is why every line rendered as "Net…" — and stacked it
 * gets the full ~58mm. A guard asserting the QR got bigger was measuring the wrong thing and failed
 * honestly; this is the property worth holding.
 */
export const textWidthMm = (cellWidthMm: number, cellHeightMm: number, qrSizeKey = 'md'): number => {
  const w = num(cellWidthMm);
  const h = num(cellHeightMm);
  const pad = cardPadMm(h);
  const contentWidth = Math.max(0, w - 2 * pad);

  if (cardLayout(w, h) !== 'wide') {
    return contentWidth; // stacked: the text owns the whole width
  }

  const qr = qrSizeMm(w, h, qrSizeKey);

  return Math.max(0, contentWidth - (qr > 0 ? qr + pad : 0));
};

// ─────────────────────────────────────────────────────────────────────────────
// FITTING A NAME — the one piece of type on a badge that must not be clipped.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * HOW WIDE A BOLD GLYPH IS, in ems, by character class.
 *
 * ⚠⚠ ONE AVERAGE RATIO CANNOT MODEL A NAME, which is what the previous two versions tried. 0.5 was
 * about right for a regular weight and far too generous for bold, so "Elena Minderova" was sized as if
 * it fit on one line and broke mid-word into "Minderov / a"; 0.62 fixed that name and is still wrong in
 * both directions for others. Measured against the bold system stack these cards actually print in, a
 * real name ranges from **0.53 em/char** ("Alexandra Konstantinopoulos" — long, lowercase, narrow) to
 * **0.77** ("Wu" — short and capital-heavy). A single number is therefore either too small for the
 * short names or too large for the long ones, and the two failures look identical in a diff.
 *
 * ⚠ The classes below were MEASURED with a canvas against `-apple-system / Segoe UI / Roboto`, the
 * stack Mantine resolves to, and each value sits a little ABOVE its class mean so the fit errs towards
 * SMALLER type. Anything unrecognised — accented Latin, Cyrillic, CJK — takes a deliberately generous
 * default, because shrinking a name slightly is recoverable and clipping it is not.
 */
const GLYPH_EM: Record<string, number> = {};

const setGlyphEm = (chars: string, em: number): void => {
  for (const ch of chars) {
    GLYPH_EM[ch] = em;
  }
};

setGlyphEm("ijlItf.,'`;:!|()[]", 0.34);
setGlyphEm(" ", 0.3);
setGlyphEm("abcdeghknopqrsuvxyz", 0.62);
setGlyphEm("mw", 0.89);
setGlyphEm("0123456789", 0.57);
setGlyphEm("ABCDEFGHJKLNOPQRSTUVXYZ", 0.78);
setGlyphEm("MW", 0.95);

/**
 * ⚠ Generous on purpose. Anything not in the table — accented Latin, Cyrillic, Greek, CJK — is sized as
 * if it were a wide capital, because a name set slightly too small is recoverable and a clipped one is
 * not, and "shrink, never clip" is the rule this whole rework was asked for.
 */
const DEFAULT_GLYPH_EM = 0.75;

/** How wide `text` is, in multiples of the point size. */
export const textEmWidth = (text: string): number => {
  let sum = 0;

  for (const ch of String(text ?? "")) {
    sum += GLYPH_EM[ch] ?? DEFAULT_GLYPH_EM;
  }

  return sum;
};

const mmToPt = (mm: number): number => (mm / 25.4) * 72;

/** Words, longest first — a name wraps BETWEEN words and never inside one. */
const wordsOf = (name: string): string[] => String(name ?? '').trim().split(/\s+/).filter(Boolean);

/**
 * How many lines `name` takes at `pt` in a column `widthMm` wide, wrapping on whitespace only.
 *
 * ⚠ Greedy line-breaking, exactly like the browser's: words are added until the next one would not
 * fit. A word that is wider than the column on its own gets its own line and OVERFLOWS — which is the
 * case `fitNamePt` exists to avoid by shrinking until it does not happen.
 */
export const nameLineCount = (name: string, widthMm: number, pt: number): number => {
  const words = wordsOf(name);

  if (words.length === 0) {
    return 0;
  }

  const widthPt = mmToPt(widthMm);
  const wordPt = (w: string) => textEmWidth(w) * pt;
  const spacePt = textEmWidth(" ") * pt;
  let lines = 1;
  let used = wordPt(words[0]);

  for (const word of words.slice(1)) {
    const next = used + spacePt + wordPt(word);

    if (next > widthPt) {
      lines += 1;
      used = wordPt(word);
    } else {
      used = next;
    }
  }

  return lines;
};

/** The widest single word, in points, at size `pt`. */
const longestWordPt = (name: string, pt: number): number =>
  wordsOf(name).reduce((max, w) => Math.max(max, textEmWidth(w) * pt), 0);

/**
 * THE POINT SIZE A NAME ACTUALLY FITS AT, given the box it has.
 *
 * ⚠⚠ IT SOLVES FOR BOTH AXES AT ONCE, which the first version did not. Sizing from the longest word
 * alone gets the width right and ignores that three wrapped lines may not fit the card's height; sizing
 * from the height alone clips long surnames. So it steps DOWN from the maximum until the longest word
 * fits the column AND the wrapped lines fit the box — the same two conditions the browser will apply.
 *
 * ⚠ It returns the largest size that fits, never a size that "nearly" fits: a badge whose name is
 * broken mid-word is unreadable at the distance a badge is read from, which is the whole point of it.
 */
export const fitNamePt = (
  name: string,
  widthMm: number,
  heightMm: number,
  opts: { maxPt?: number; minPt?: number; lineHeight?: number } = {},
): number => {
  const { maxPt = 34, minPt = 8, lineHeight = 1.08 } = opts;
  const heightPt = mmToPt(heightMm);
  const widthPt = mmToPt(widthMm);

  if (!wordsOf(name).length || widthMm <= 0 || heightMm <= 0) {
    return minPt;
  }

  for (let pt = maxPt; pt >= minPt; pt -= 0.5) {
    const fitsWidth = longestWordPt(name, pt) <= widthPt;
    const fitsHeight = nameLineCount(name, widthMm, pt) * pt * lineHeight <= heightPt;

    if (fitsWidth && fitsHeight) {
      return pt;
    }
  }

  return minPt;
};

/**
 * A FOLDED card's fold line, in mm from the top, or 0 when the stock does not fold.
 *
 * ⚠ Avery 5011 place cards FOLD IN HALF. The flat cell is 3.75 × 2.875in, and the finished card is half
 * that tall — so the sheet is only correct if the TOP half prints UPSIDE DOWN. Printed flat, as this
 * first shipped, the card reads correctly on one side and is blank-then-inverted on the other.
 */
export const foldMm = (media?: CardMediaGeometry | null): number => num(media?.settings?.foldMm);

export const cardFolds = (media?: CardMediaGeometry | null): boolean => foldMm(media) > 0;

/**
 * ⚠ MIRRORS `PRINT_DESIGN_KIND.PLACE_CARD`. This file has no imports on purpose (see the header), so
 * the one string it needs is written out and pinned by `check-print-sheets` against the real constant.
 */
const PLACE_CARD_KIND = 'place_card';

/**
 * DOES THIS STOCK SUIT THIS KIND OF CARD? Two rules, and the second is the one that needed the owner to
 * say it out loud (2026-09-23):
 *
 *   - ⚠⚠ STOCK THAT FOLDS IS A TENT CARD AND NOTHING ELSE. Avery 5011 was showing up in the name-badge
 *     stock list, where choosing it prints a badge whose top half is upside down — the fold is not a
 *     decoration, it is what the paper IS.
 *   - ⚠ TICKET SHEETS ARE FOR TICKETS. That one is NOT derivable — 16431 and 16430 are just perforated
 *     paper and nothing in their geometry objects to a name badge — so the seed lists `kinds` on those
 *     three rows. The asymmetry is deliberate and is the owner's: a ticket may be printed on badge
 *     stock, a badge may not be printed on ticket stock.
 *
 * ⚠ A HOST'S OWN STOCK CARRIES NO `kinds` AND SUITS EVERYTHING NON-FOLDING. Defaulting to "nothing"
 * would make every stock somebody adds invisible until they worked out which undocumented key to set.
 */
export const mediaSuitsKind = (media: CardMediaGeometry | null | undefined, kind: string): boolean => {
  if (cardFolds(media)) {
    return kind === PLACE_CARD_KIND;
  }

  const kinds = media?.settings?.kinds;

  if (Array.isArray(kinds)) {
    return kinds.includes(kind);
  }

  return kind !== PLACE_CARD_KIND;
};
